import { type FC } from 'react';
import { ExternalLink, MapPin, Plane, Car } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import type { HotelPartner } from '@/types';

export interface HotelDetailDialogProps {
  hotel: HotelPartner | null;
  onClose: () => void;
}

export const HotelDetailDialog: FC<HotelDetailDialogProps> = ({ hotel, onClose }) => {
  const handleOpenChange = (next: boolean): void => {
    if (!next) onClose();
  };

  if (!hotel) return null;

  const showPerks = Boolean(hotel.hotelPerksText) || (hotel.amenities && hotel.amenities.length > 0);

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 border-turquoise-600 text-white">
        <section className="relative min-h-[280px] flex items-center justify-center overflow-hidden">
          {hotel.backgroundImageUrl && (
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url('${hotel.backgroundImageUrl}')` }}
            >
              <div className="absolute inset-0 bg-black/50"></div>
            </div>
          )}
          <div className="relative z-10 px-6 py-10 text-center text-white">
            <DialogTitle className="text-4xl font-viking mb-3 drop-shadow-lg text-white">{hotel.name}</DialogTitle>
            <p className="text-lg mb-4 drop-shadow-md">
              {hotel.address}, {hotel.city}, {hotel.state} {hotel.zipCode}
            </p>
            {hotel.bookingUrl && (
              <Button
                size="lg"
                variant="default"
                onClick={() => window.open(hotel.bookingUrl, '_blank', 'noopener,noreferrer')}
              >
                <ExternalLink className="h-5 w-5 mr-2" />
                Book Your Room
              </Button>
            )}
          </div>
        </section>

        <div className="px-6 py-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl font-viking">
                <Plane className="h-6 w-6 text-orange-500" />
                Getting Here
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {hotel.gettingHereText ? (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: hotel.gettingHereText }}
                />
              ) : (
                <>
                  <p>
                    Flying in for the Cactus Cup? You're in luck: the{' '}
                    <strong>{hotel.name}</strong> is{' '}
                    {hotel.distanceFromAirport || 'conveniently located near'} Phoenix Sky Harbor International Airport.
                  </p>
                  {hotel.parkingInfo && <p>{hotel.parkingInfo}</p>}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl font-viking">
                <MapPin className="h-6 w-6 text-orange-500" />
                Staying Here
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {hotel.stayingHereText ? (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: hotel.stayingHereText }}
                />
              ) : (
                <>
                  <p>
                    Stay where the action is! We've partnered with the {hotel.name} to offer an exclusive Cactus Cup group rate.
                  </p>
                  {hotel.rateDescription && (
                    <p className="font-semibold text-orange-500">{hotel.rateDescription}</p>
                  )}
                  {hotel.bookingCode && (
                    <p className="bg-turquoise-900/40 border border-turquoise-500/40 p-3 rounded">
                      <strong>Booking Code:</strong>{' '}
                      <code className="text-orange-500">{hotel.bookingCode}</code>
                    </p>
                  )}
                </>
              )}
              {hotel.bookingUrl && (
                <Button
                  variant="default"
                  onClick={() => window.open(hotel.bookingUrl, '_blank', 'noopener,noreferrer')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Book Your Room Here
                </Button>
              )}
            </CardContent>
          </Card>

          {showPerks && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl font-viking">
                  <Car className="h-6 w-6 text-orange-500" />
                  Hotel Perks
                </CardTitle>
              </CardHeader>
              <CardContent>
                {hotel.hotelPerksText ? (
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: hotel.hotelPerksText }}
                  />
                ) : (
                  <>
                    <p className="mb-4">
                      The {hotel.name} has everything you need to relax and recharge between tournaments:
                    </p>
                    {hotel.amenities && hotel.amenities.length > 0 && (
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {hotel.amenities.map((amenity, index) => (
                          <li key={index} className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>{amenity}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
